/**
 * Role Switching Service
 * Handles switching between user roles when user has multiple roles
 */

import { apiClient } from './apiClient';
import { store } from '../store/store';
import { setUser, setTokens } from '../store/slices/authSlice';
import { authPersistence } from './authPersistence';
import { User } from '../store/slices/authSlice';

export interface RoleSwitchResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    tokens: {
      access_token: string;
      refresh_token: string;
      token_type: string;
      access_token_expires_at: string;
    };
    active_role: string;
    available_roles: string[];
  };
}

class RoleSwitchingService {
  /**
   * Switch user's active role
   * @param targetRole - The role to switch to
   * @returns Promise with updated user and tokens
   */
  async switchRole(targetRole: 'admin' | 'host' | 'guest' | 'usher'): Promise<RoleSwitchResponse> {
    try {
      const response = await apiClient.post<RoleSwitchResponse>('/auth/switch-role/', {
        target_role: targetRole,
      });

      if (response.data.success) {
        const { user, tokens, active_role, available_roles } = response.data.data;

        // Update Redux store
        const updatedUser: User = {
          ...user,
          roles: available_roles,
          active_role: active_role,
        };

        store.dispatch(setUser(updatedUser));
        store.dispatch(setTokens(tokens));

        // Update localStorage
        const currentState = store.getState();
        authPersistence.storeAuthData({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          token_type: tokens.token_type,
          access_token_expires_at: tokens.access_token_expires_at,
          user: {
            ...updatedUser,
            email: updatedUser.email || '',
          },
          remember_me: currentState.auth.rememberMe,
          last_activity: new Date().toISOString(),
        });

        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to switch role');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to switch role';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get available roles for current user
   * @returns Array of available roles
   */
  getAvailableRoles(): string[] {
    const state = store.getState();
    const user = state.auth.user;
    
    if (!user) {
      return [];
    }

    // Return roles array if available, otherwise return single role
    return user.roles || [user.role];
  }

  /**
   * Check if user can switch to a specific role
   * @param targetRole - The role to check
   * @returns True if user has the target role
   */
  canSwitchToRole(targetRole: string): boolean {
    const availableRoles = this.getAvailableRoles();
    return availableRoles.includes(targetRole);
  }

  /**
   * Get current active role
   * @returns Current active role or primary role
   */
  getCurrentActiveRole(): string {
    const state = store.getState();
    const user = state.auth.user;
    
    if (!user) {
      return '';
    }

    return user.active_role || user.role;
  }
}

export const roleSwitchingService = new RoleSwitchingService();
export default roleSwitchingService;

